<template>
  <div class="container mt-6 text-white">
    <h2 class="font-medium text-center uppercase">Explore the Performing Glastonbury archive</h2>
    <ais-instant-search
      :search-client="searchClient"
      :index-name="INDEX"
      :routing="routing"
      :middlewares="middlewares"
      :search-function="onSearch"
      class="flex flex-wrap -mx-4"
    >
      <ais-configure :hits-per-page.camel="12" />
      <ais-search-box
        :autofocus="true"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="none"
        placeholder="Search the archive…"
        class="p-4 w-full order-1"
        spellcheck="false"
      >
        <template #submit-icon>
          Search
          <svg class="w-2 transform ml-3" viewBox="0 0 24 24">
            <use xlink:href="#icon-triangle" />
          </svg>
        </template>
      </ais-search-box>
      <section class="p-4 w-full md:w-2/3 lg:w-3/4 order-3">
        <div class="mb-4 flex flex-wrap">
          <ais-stats class="w-full md:w-2/3 lg:w-3/4">
            <template #default="{ nbPages, nbHits, page, query }">
              <div class="flex items-center justify-between">
                <p class="md:hidden">
                  Page {{ page + 1 }} of {{ nbHits }} results
                </p>
                <p class="hidden md:inline">
                  {{ nbHits }} records found<template v-if="query">
                    for <q>{{ query }}</q></template
                  >. Showing page {{ page + 1 }} of {{ nbPages }}
                </p>
                <button
                  aria-controls="accordion-item-filters"
                  aria-label="filter results"
                  class="bg-transparent btn-toggle border border-golden-fizz flex items-center justify-between md:hidden p-2 cursor-pointer w-auto rounded-full"
                  data-toggle
                  data-target="#accordion-item-filters"
                  type="button">
                  <svg class="icon-open w-6" viewBox="0 0 24 24" role="img">
                    <use xlink:href="#icon-filters" />
                  </svg>
                </button>
              </div>
            </template>
          </ais-stats>
          <ais-hits-per-page
            :items="[
              { label: '10 per page', value: 10, default: true },
              { label: '20 per page', value: 20 },
              { label: '50 per page', value: 50 },
            ]"
            class="hidden md:inline text-right md:w-1/3 lg:w-1/4"
          />
          <ais-current-refinements :transform-items="labelRefinements" class="w-full" />
        </div>
        <ais-hits>
          <template #item="{ item }">
            <component :is="hitComponent(item.contentType)" :item="item" />
          </template>
        </ais-hits>
        <div class="pt-4">
          <ais-pagination :padding="2" :show-first="false" :show-last="false" scroll-to="body" />
        </div>
      </section>
      <aside class="hidden md:block p-4 w-full md:w-1/3 lg:w-1/4 order-2 md:order-2">
        <header class="mb-4">
          <div  class="flex items-center justify-between">
            <h3 class="h--sub">Refine results</h3>
            <button
              aria-controls="accordion-item-filters"
              aria-label="filter results"
              class="bg-transparent btn-toggle border border-golden-fizz flex items-center justify-between md:hidden p-2 cursor-pointer w-auto rounded-full"
              data-toggle
              data-target="#accordion-item-filters"
              type="button">
              <svg class="icon-open w-6" viewBox="0 0 24 24" role="img">
                <use xlink:href="#icon-filters" />
              </svg>
            </button>
          </div>
          <ais-clear-refinements>
            <template #default="{ canRefine, refine }">
              <button v-if="canRefine" type="button" @click.prevent="() => clearFilters(refine)">
                Reset all
              </button>
            </template>
          </ais-clear-refinements>
          <!--      <ais-clear-refinements>
            <template #resetLabel>Reset all</template>
          </ais-clear-refinements> -->
        </header>
        <ais-experimental-dynamic-widgets>
          <!-- Recordtype -->
          <ais-panel>
            <template #header="{ hasRefinements }">
              <h3 v-if="hasRefinements">Record Type</h3>
            </template>
            <template #default>
              <ais-menu
                :transform-items="transformRefinementItems"
                attribute="contentTypeForDisplay"
              />
            </template>
          </ais-panel>
          <!-- Stage filter -->
          <ais-panel>
            <template #header="{ hasRefinements }">
              <h3 v-if="hasRefinements">Stage</h3>
            </template>
            <template #default>
              <ais-refinement-list
                :searchable="true"
                :limit="5"
                :show-more="true"
                :show-more-limit="15"
                :transform-items="transformRefinementItems"
                attribute="stage.title"
                searchable-placeholder="Search stages"
              />
            </template>
          </ais-panel>
          <!--  Day filter -->
          <ais-panel>
            <template #header="{ hasRefinements }">
              <h3 v-if="hasRefinements">Day</h3>
            </template>
            <template #default>
              <ais-refinement-list
                :transform-items="transformRefinementItems"
                attribute="dayOfTheWeek"
              />
            </template>
          </ais-panel>
          <!-- Decade filter -->
          <ais-panel>
            <template #header="{ hasRefinements }">
              <h3 v-if="hasRefinements">Decade</h3>
            </template>
            <template #default>
              <ais-refinement-list
                :transform-items="transformRefinementItems"
                attribute="displayDecade"
              />
            </template>
          </ais-panel>
          <!-- Contributor names filter -->
          <ais-panel>
            <template #header="{ hasRefinements }">
              <h3 v-if="hasRefinements">Contributors</h3>
            </template>
            <template #default>
              <ais-refinement-list
                :searchable="true"
                :limit="5"
                :show-more="true"
                :show-more-limit="15"
                :transform-items="transformRefinementItems"
                operator="and"
                attribute="contributorNames"
                searchable-placeholder="Search Contributors"
              />
            </template>
          </ais-panel>
          <ais-panel>
            <template #header="{ hasRefinements }">
              <h3 v-if="hasRefinements">Year</h3>
            </template>
            <template #default>
              <ais-range-input attribute="year">
                <template #default="{ range, refine }">
                  <div class="pt-8 pb-4 pl-6 pr-6">
                    <slider
                      v-model="yearSlider.value"
                      :min="range.min"
                      :max="range.max"
                      @change="($e) => refine({ min: $e[0], max: $e[1] })"
                    />
                  </div>
                </template>
              </ais-range-input>
            </template>
          </ais-panel>
        </ais-experimental-dynamic-widgets>
      </aside>
    </ais-instant-search>
  </div>
</template>
<script>
import algoliasearch from "algoliasearch/lite";
// import "instantsearch.css/themes/reset.css";
// import "instantsearch.css/themes/satellite-min.css";
import "@vueform/slider/themes/default.css";

import Slider from "@vueform/slider";

import ContributorHit from "@/vue-components/hits/ContributorHit";
import EventDayHit from "@/vue-components/hits/EventDayHit";
import EventYearHit from "@/vue-components/hits/EventYearHit";
import EventYearDayHit from "@/vue-components/hits/EventYearDayHit";
import PerformanceHit from "@/vue-components/hits/PerformanceHit";
import StageHit from "@/vue-components/hits/StageHit";
import StageYearDayHit from "@/vue-components/hits/StageYearDayHit";
import StageYearHit from "@/vue-components/hits/StageYearHit";

const { APPLICATION, INDEX, SEARCH_KEY } = window.APP.ALGOLIA;

export default {
  components: {
    ContributorHit,
    EventDayHit,
    EventYearHit,
    EventYearDayHit,
    PerformanceHit,
    StageHit,
    StageYearDayHit,
    StageYearHit,
    Slider,
  },
  emits: ["search"],
  data() {
    const minYear = 1970;
    const maxYear = new Date().getFullYear();
    return {
      INDEX,
      hasSearched: false,
      algoliaHelper: null,
      algoliaUiState: {},
      $instantSearchInstance: null,
      searchClient: algoliasearch(APPLICATION, SEARCH_KEY),
      facetDict: {
        contentTypeForDisplay: "Record Type",
        displayDecade: "Decade",
        dayOfTheWeek: "Day",
        contributorNames: "Contributors",
        "stage.title": "Stage",
        year: "Years",
      },
      yearSlider: {
        value: [minYear, maxYear],
        min: minYear,
        max: maxYear,
      },
      routing: {
        stateMapping: {
          stateToRoute(uiState) {
            const indexUiState = uiState[INDEX];
            return {
              q: indexUiState.query,
              p: indexUiState.page,
              perPage: indexUiState.hitsPerPage,

              type: indexUiState.menu && indexUiState.menu.contentTypeForDisplay,

              stage: indexUiState.refinementList && indexUiState.refinementList["stage.title"],
              day: indexUiState.refinementList && indexUiState.refinementList.dayOfTheWeek,
              decade: indexUiState.refinementList && indexUiState.refinementList.displayDecade,
              contributors:
                indexUiState.refinementList && indexUiState.refinementList.contributorNames,

              year: indexUiState.range && indexUiState.range.year,
            };
          },
          routeToState(routeState) {
            return {
              [INDEX]: {
                query: routeState.q,
                page: routeState.p,
                hitsPerPage: routeState.perPage,
                menu: {
                  contentTypeForDisplay: routeState.type,
                },
                refinementList: {
                  "stage.title": routeState.stage,
                  dayOfTheWeek: routeState.day,
                  displayDecade: routeState.decade,
                  contributorNames: routeState.contributors,
                  year: routeState.year,
                },
                range: {
                  year: routeState.year,
                },
              },
            };
          },
        },
      },
    };
  },
  computed: {
    middlewares() {
      return [this.yearSliderMiddleware];
    },
  },
  watch: {
    ["algoliaUiState.range"](newRanges) {
      if (!newRanges || !newRanges.year) {
        this.yearSlider.value = [this.yearSlider.min, this.yearSlider.max];
      } else {
        this.yearSlider.value = this.parseRange(newRanges.year);
      }
    },
  },
  methods: {
    hitComponent: (contentType) => `${contentType}Hit`,
    onSearch(helper) {
      if (!this.algoliaHelper) {
        this.algoliaHelper = helper;
      }
      if (this.hasRefinements(helper)) {
        if (helper.state.numericRefinements.year) {
          this.updateYearSliderFromHelperState(helper.state.numericRefinements.year);
        }
        this.hasSearched = true;
        this.$emit("search");
      }
      helper.search();
    },
    hasRefinements(helper) {
      // don't search if we don't have a query, refinements or a page, or we have searched before, or forcing show
      return (
        !!helper.getQuery().query ||
        !!helper.getPage() ||
        Object.values(helper.state.disjunctiveFacetsRefinements).some((values) => values.length) ||
        Object.values(helper.state.hierarchicalFacetsRefinements).some((values) => values.length) ||
        Object.values(helper.state.numericRefinements).some(
          (refinement) => Object.keys(refinement).length
        )
      );
    },
    labelRefinements(items) {
      return items.map((item) => {
        return {
          ...item,
          label: this.facetDict[item.label],
        };
      });
    },
    yearSliderMiddleware({ instantSearchInstance }) {
      this.$instantSearchInstance = instantSearchInstance;

      return {
        subscribe: () => {
          this.algoliaUiState = this.$instantSearchInstance.getUiState()[INDEX];
        },
        onStateChange: ({ uiState }) => {
          this.algoliaUiState = uiState[INDEX];
        },
      };
    },
    clearFilters(reset) {
      reset();
      this.resetYearSlider();
    },
    resetYearSlider() {
      this.yearSlider.value = [this.yearSlider.min, this.yearSlider.max];
    },
    parseRange(rangeStr) {
      if (typeof rangeStr === "undefined") {
        return [this.yearSlider.min, this.yearSlider.max];
      }
      const [min, max] = rangeStr.split(":");

      return [
        Number.isNaN(parseInt(min, 10)) ? this.yearSlider.min : parseInt(min, 10),
        Number.isNaN(parseInt(max, 10)) ? this.yearSlider.max : parseInt(max, 10),
      ];
    },
    updateYearSliderFromHelperState(helperYearState) {
      let min = this.yearSlider.min;
      let max = this.yearSlider.max;

      Object.entries(helperYearState).forEach(([key, value]) => {
        if (key === "<=") {
          max = value[0];
        }
        if (key === ">=") {
          min = value[0];
        }

        this.yearSlider.value = [min, max];
      });
    },
    transformRefinementItems(items) {
      return items.filter(Boolean);
    },
  },
};
</script>
